import { Currency, Commission, PaymentMethod, EnumValues, PaymentCommission } from 'common/types';
import { Plan, SubscriptionPlan } from 'common/modules/plans';
import { PaymentCurrencyCode, TrustlyCurrencyCode } from './currencies';
import { OnlinePaymentMethodWithManual } from 'common/modules/payments/types';
import { stringEnum } from 'common/utils/objects';
import { isOnlinePaymentMethod } from 'common/modules/payments/paymentMethods';

export const PaymentPlans = stringEnum(['LITE', 'CORE', 'BASIC', 'GROW', 'ADVANCED']);

export type PaymentPlan = EnumValues<typeof PaymentPlans>;

export const planToPaymentPlan = (plan?: Plan): PaymentPlan => {
	const PlanToPaymentPlanMap: Record<Plan, PaymentPlan> = {
		LITE: 'LITE',
		BASIC: 'BASIC',
		GROW: 'GROW',
		ADVANCED: 'ADVANCED',
		ENTERPRISE: 'ADVANCED',
		CORE_COMMISSION: 'CORE',
		CORE_DEMO: 'CORE',
		CORE_FIXED: 'CORE',
		CORE_INTERNAL_ONLY: 'CORE',
	};
	return !!plan ? PlanToPaymentPlanMap[plan] ?? PaymentPlans.CORE : PaymentPlans.CORE;
};

type CommissionByPlan<T extends PaymentCurrencyCode = PaymentCurrencyCode> = Record<
	PaymentPlan,
	PaymentMethodCommission<T>
>;

interface PaymentMethodCommission<K extends PaymentCurrencyCode = PaymentCurrencyCode> {
	fixedInCents: Record<K, number>;
	percentageInDecimals: number;
}

const NO_COMMISSION: CommissionByPlan<'EUR'> = {
	CORE: {
		percentageInDecimals: 0,
		fixedInCents: {
			EUR: 0,
		},
	},
	LITE: {
		percentageInDecimals: 0,
		fixedInCents: {
			EUR: 0,
		},
	},
	BASIC: {
		percentageInDecimals: 0,
		fixedInCents: {
			EUR: 0,
		},
	},
	GROW: {
		percentageInDecimals: 0,
		fixedInCents: {
			EUR: 0,
		},
	},
	ADVANCED: {
		percentageInDecimals: 0,
		fixedInCents: {
			EUR: 0,
		},
	},
};

const CARD_ONLINE: CommissionByPlan = {
	CORE: {
		percentageInDecimals: 0.025,
		fixedInCents: {
			EUR: 35,
			USD: 40,
			CZK: 950,
			SEK: 400,
			CHF: 40,
			HRK: 300,
			GBP: 35,
			NOK: 400,
			DKK: 300,
			PLN: 160,
		},
	},
	LITE: {
		percentageInDecimals: 0.049,
		fixedInCents: {
			EUR: 45,
			USD: 50,
			CHF: 45,
			CZK: 1105,
			DKK: 335,
			GBP: 40,
			HRK: 340,
			NOK: 440,
			SEK: 470,
			PLN: 225,
		},
	},
	BASIC: {
		percentageInDecimals: 0.039,
		fixedInCents: {
			EUR: 40,
			USD: 45,
			CHF: 40,
			CZK: 995,
			DKK: 305,
			GBP: 35,
			HRK: 310,
			NOK: 395,
			SEK: 420,
			PLN: 200,
		},
	},
	GROW: {
		percentageInDecimals: 0.029,
		fixedInCents: {
			EUR: 30,
			USD: 35,
			CHF: 30,
			CZK: 770,
			DKK: 235,
			GBP: 25,
			HRK: 240,
			NOK: 305,
			SEK: 330,
			PLN: 155,
		},
	},
	ADVANCED: {
		percentageInDecimals: 0.019,
		fixedInCents: {
			EUR: 25,
			USD: 25,
			CHF: 25,
			CZK: 550,
			DKK: 170,
			GBP: 20,
			HRK: 170,
			NOK: 220,
			SEK: 235,
			PLN: 110,
		},
	},
};

const CARD_ONLINE_STRIPE: CommissionByPlan = {
	CORE: {
		percentageInDecimals: 0.015,
		fixedInCents: {
			EUR: 0,
			USD: 0,
			CZK: 0,
			SEK: 0,
			CHF: 0,
			HRK: 0,
			GBP: 0,
			NOK: 0,
			DKK: 0,
			PLN: 0,
		},
	},
	LITE: {
		percentageInDecimals: 0.03,
		fixedInCents: {
			EUR: 0,
			USD: 0,
			CZK: 0,
			SEK: 0,
			CHF: 0,
			HRK: 0,
			GBP: 0,
			NOK: 0,
			DKK: 0,
			PLN: 0,
		},
	},
	BASIC: {
		percentageInDecimals: 0.02,
		fixedInCents: {
			EUR: 0,
			USD: 0,
			CZK: 0,
			SEK: 0,
			CHF: 0,
			HRK: 0,
			GBP: 0,
			NOK: 0,
			DKK: 0,
			PLN: 0,
		},
	},
	GROW: {
		percentageInDecimals: 0.01,
		fixedInCents: {
			EUR: 0,
			USD: 0,
			CZK: 0,
			SEK: 0,
			CHF: 0,
			HRK: 0,
			GBP: 0,
			NOK: 0,
			DKK: 0,
			PLN: 0,
		},
	},
	ADVANCED: {
		percentageInDecimals: 0.005,
		fixedInCents: {
			EUR: 0,
			USD: 0,
			CZK: 0,
			SEK: 0,
			CHF: 0,
			HRK: 0,
			GBP: 0,
			NOK: 0,
			DKK: 0,
			PLN: 0,
		},
	},
};

const TRUSTLY: CommissionByPlan<TrustlyCurrencyCode> = {
	CORE: {
		percentageInDecimals: 0.01,
		fixedInCents: {
			EUR: 70,
			SEK: 750,
			NOK: 800,
			DKK: 550,
		},
	},
	LITE: {
		percentageInDecimals: 0.02,
		fixedInCents: {
			EUR: 100,
			DKK: 745,
			NOK: 980,
			SEK: 1045,
		},
	},
	BASIC: {
		percentageInDecimals: 0.0175,
		fixedInCents: {
			EUR: 90,
			DKK: 670,
			NOK: 880,
			SEK: 940,
		},
	},
	GROW: {
		percentageInDecimals: 0.0125,
		fixedInCents: {
			EUR: 80,
			DKK: 585,
			NOK: 785,
			SEK: 835,
		},
	},
	ADVANCED: {
		percentageInDecimals: 0.0075,
		fixedInCents: {
			EUR: 70,
			DKK: 520,
			NOK: 685,
			SEK: 730,
		},
	},
};

const PAYMENT_METHOD_COMMISSIONS: Record<
	OnlinePaymentMethodWithManual,
	{
		LITE: PaymentMethodCommission<'EUR'>;
		CORE: PaymentMethodCommission<'EUR'>;
		BASIC: PaymentMethodCommission<'EUR'>;
		GROW: PaymentMethodCommission<'EUR'>;
		ADVANCED: PaymentMethodCommission<'EUR'>;
	}
> = {
	CARD_ONLINE,
	TRUSTLY,
	PAYTRAIL: TRUSTLY,
	MOBILEPAY: CARD_ONLINE,
	GOOGLEPAY: CARD_ONLINE,
	APPLEPAY: CARD_ONLINE,
	CARD_ONLINE_STRIPE,
	// No commission methods
	EPASSI_ONLINE: NO_COMMISSION,
	PAY_STORE: NO_COMMISSION,
	MANUAL: NO_COMMISSION,
};

const getPaymentMethodCommissions = (method: OnlinePaymentMethodWithManual) => {
	return PAYMENT_METHOD_COMMISSIONS[method];
};

export const getStripeCommission = (plan: SubscriptionPlan) => {
	const commissionPercentage = (CARD_ONLINE_STRIPE[plan].percentageInDecimals * 100).toFixed(1);
	return `${commissionPercentage}%`;
};

export const getDefaultCommissionPercentageInDecimals = (
	plan: PaymentPlan,
	method: PaymentMethod,
) => {
	if (!isOnlinePaymentMethod(method)) {
		return 0;
	}
	const paymentMethodCommissionObject = getPaymentMethodCommissions(method);
	return paymentMethodCommissionObject[plan].percentageInDecimals;
};

export const getDefaultFixedCommissionInCents = (
	plan: PaymentPlan,
	currency: Currency,
	method: PaymentMethod,
) => {
	if (!isOnlinePaymentMethod(method)) {
		return {
			currency,
			fixedInCents: 0,
		};
	}
	const paymentMethodCommissionObject = getPaymentMethodCommissions(method);
	const fixedInCentsObject = paymentMethodCommissionObject[plan].fixedInCents;

	const defaultFixedInCents = fixedInCentsObject[currency];
	return defaultFixedInCents !== undefined
		? {
				currency,
				fixedInCents: defaultFixedInCents,
		  }
		: {
				currency: 'EUR',
				fixedInCents: fixedInCentsObject.EUR,
		  };
};

export const getDefaultCommission = (
	plan: PaymentPlan,
	paymentCurrency: string,
	method: PaymentMethod,
): Commission => {
	const percentageInDecimals = getDefaultCommissionPercentageInDecimals(plan, method);
	const { currency, fixedInCents } = getDefaultFixedCommissionInCents(
		plan,
		paymentCurrency,
		method,
	);
	return {
		currency,
		fixedInCents,
		percentageInDecimals,
	};
};

export const getShopCustomPaymentCommission = (
	method: PaymentMethod,
	shopPaymentCommission?: PaymentCommission,
): Commission | undefined => {
	const paymentMethod = method === 'APPLEPAY' || method === 'GOOGLEPAY' ? 'CARD_ONLINE' : method;
	return shopPaymentCommission?.[paymentMethod];
};
